import type { ConditionDraft } from '@hygge/shared';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ValueEditor } from './value-editor';
import { X } from 'lucide-react';

interface ConditionRowProps {
  boardId: string;
  condition: ConditionDraft;
  onChange: (condition: ConditionDraft) => void;
  onRemove: () => void;
}

const CONDITION_FIELDS = [
  { value: 'status', label: 'Status' },
  { value: 'statusCategory', label: 'Status category' },
  { value: 'priority', label: 'Priority' },
  { value: 'type', label: 'Type' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'reporter', label: 'Reporter' },
  { value: 'label', label: 'Label' },
  { value: 'storyPoints', label: 'Story points' },
  { value: 'dueDate', label: 'Due date' },
  { value: 'text', label: 'Text' },
  { value: 'hasParent', label: 'Has parent' },
  { value: 'inSprint', label: 'In sprint' },
] as const;

const OPERATORS = [
  { value: 'eq', label: 'is' },
  { value: 'neq', label: 'is not' },
  { value: 'in', label: 'is one of' },
  { value: 'isEmpty', label: 'is empty' },
  { value: 'isNotEmpty', label: 'is set' },
  { value: 'gt', label: 'greater than' },
  { value: 'lt', label: 'less than' },
  { value: 'contains', label: 'contains' },
  { value: 'overdue', label: 'is overdue' },
  { value: 'withinDays', label: 'within days' },
] as const;

type Field = ConditionDraft['field'];
type Operator = ConditionDraft['operator'];

export function ConditionRow({ boardId, condition, onChange, onRemove }: ConditionRowProps) {
  return (
    <div className="flex items-center gap-2">
      <Select
        value={condition.field || undefined}
        onValueChange={(val) => onChange({ field: val as Field, operator: 'eq', value: undefined })}
      >
        <SelectTrigger className="h-9 flex-1" aria-label="Condition field">
          <SelectValue placeholder="Choose field…" />
        </SelectTrigger>
        <SelectContent>
          {CONDITION_FIELDS.map((f) => (
            <SelectItem key={f.value} value={f.value}>
              {f.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={condition.operator}
        onValueChange={(val) =>
          onChange({ ...condition, operator: val as Operator, value: undefined })
        }
      >
        <SelectTrigger className="h-9 flex-1" aria-label="Operator">
          <SelectValue placeholder="Operator" />
        </SelectTrigger>
        <SelectContent>
          {OPERATORS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <ValueEditor
        boardId={boardId}
        condition={condition}
        onChange={(value) => onChange({ ...condition, value })}
      />

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-9 w-9 shrink-0"
        onClick={onRemove}
        aria-label="Remove condition"
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
